import React, { useRef, useState } from 'react';
import { createBill, startESign, finalizeESign, getESignStatus, getBillSignRecord } from '../api';
import useESignWatcher from '../../../hooks/useESignWatcher';
import aurikshaLogo from '../../../assets/auriksha_logo.png';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Button, Card, Divider, message, Row, Col, Grid, Modal, Spin, Alert } from 'antd';
import { calculateGST } from '../utils/gst';
import companyInfo from '../../../jsons/companyInfo.json';
import CompanyInfo from '../components/CompanyInfo';
import CustomerDetails from '../components/CustomerDetails';
import GoldItemsTable from '../components/GoldItemsTable';
import PaymentAndRemarks from '../components/PaymentAndRemarks';
import GoldImageCapture from '../components/GoldImageCapture';
import "bootstrap/dist/css/bootstrap.min.css";
import GoldPurchaseDeclaration from '../components/GoldPurchaseDeclaration';
import '../styles/print.css';
import html2pdf from 'html2pdf.js';
import leegalityConfig from '../../../constants/leegality.config';

const { Title } = Typography;
const { useBreakpoint } = Grid;

const CreateBill = () => {
  const location = useLocation();
  const screens = useBreakpoint();
  const customer = location.state?.consolidated?.customer;
  const ratePerGram = location.state?.consolidated?.scheme?.ratePerGram;
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const generateBillId = () =>
    `${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${Math.random()
      .toString(36)
      .substring(2, 8)
      .toUpperCase()}`;

  const [form, setForm] = useState({
    aadhaarNo: customer?.aadhaar || '',
    goldDetails: [
      {
        itemName: '',
        purity: '',
        netWeight: 0,
        grossWeight: 0,
        deduction: 0,
        dustDeduction: 0,
        ratePerGram: ratePerGram || 0,
        totalPrice: 0,
      },
    ],
    paymentMode: 'CASH',
    remarks: '',
    branchLocation: '',
    billId: generateBillId(),
    billDate: new Date(),
    dueDate: new Date(),
    salesRepName: 'Our company name',
    customerId: customer?._id || '',
    processingFees: 0,
    images: [],
    customerImage: null,
  });

  const [eSignState, setESignState] = useState({
    isLoading: false,
    requestId: null,
    documentId: null,
    signUrl: null,
    modalVisible: false,
    startMessage: '',
  });

  const mapAadhaarGender = (gender) => {
    if (!gender) return '';
    const normalized = String(gender).trim().toLowerCase();
    const mapping = {
      male: 'M',
      m: 'M',
      female: 'F',
      f: 'F',
      transgender: 'T',
      other: 'T',
      t: 'T'
    };
    return mapping[normalized] || '';
  };

  const handleGoldDetailsChange = (updatedGoldDetails) => {
    setForm({ ...form, goldDetails: updatedGoldDetails });
  };

  const generateBillPDF = async () => {
    const billElement = document.getElementById('bill-to-print');
    if (!billElement) {
      throw new Error('Bill element not found');
    }

    // Temporarily hide .no-print elements so they don't appear in the PDF
    const noPrintEls = billElement.querySelectorAll('.no-print');
    noPrintEls.forEach((el) => (el.style.display = 'none'));

    try {
      // Configure html2pdf options
      const opt = {
        margin: 0,
        filename: `Bill_${form.billId}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      // Generate PDF and get as blob
      const pdf = await html2pdf().set(opt).from(billElement).outputPdf('blob');

      // Convert blob to base64
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdf);
      });
    } finally {
      // Restore .no-print elements
      noPrintEls.forEach((el) => (el.style.display = ''));
    }
  };

  const handleESignSubmit = async () => {
    try {
      const aadhaarGender = mapAadhaarGender(customer?.gender);
      if (!aadhaarGender) {
        messageApi.error('Customer gender is required for Aadhaar verification. Please update the customer profile.');
        return;
      }

      // setESignState({ ...eSignState, isLoading: true });
      messageApi.loading('Generating PDF for eSign...');

      // Generate PDF and convert to base64
      const fileBase64 = await generateBillPDF();

      if (!fileBase64) {
        throw new Error('Failed to generate bill PDF');
      }

      // TEMPORARY TEST: Download the PDF for testing
      const blob = new Blob([Uint8Array.from(atob(fileBase64), c => c.charCodeAt(0))], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Test_Bill_${form.billId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      console.log('✅ [TEST] PDF downloaded for testing');



      messageApi.loading('Starting eSign process...');
      // Format date for display
      const formattedDate = new Date(form.billDate).toLocaleDateString('en-IN');
      // Prepare eSign request data with proper Leegality field mapping
      const eSignData = {
        // Leegality specific fields
        profileId: leegalityConfig.eSign.profileId,
        file: {
          name: `Agreement_Document_${form.billId}`,
          file: fileBase64,
        },
        invitees: [
          {
            name: customer?.name || "",
            email: customer?.email || "",
            phone: customer?.phone || "",
            aadhaarConfig: {
              verifyPincode: customer?.pincode || "",
              verifyState: customer?.state || "",
              verifyYob: customer?.dateOfBirth ? new Date(customer.dateOfBirth).getFullYear().toString() : "",
              verifyGender: aadhaarGender
            },
            // Webhook / redirect URLs are set by the backend (/esign/start).
          }
        ],
        irn: form.billId || "",
      };

      console.log('@@ Prepared eSign data:', JSON.stringify(eSignData, null, 2));
      const paylod = {
        billPayload: form,
        eSignData: eSignData,
      };

      console.log('@@ eSign payload:', JSON.stringify(paylod, null, 2));

      // Start eSign request
      const result = await startESign(paylod);

      const invitee = result?.data?.invitees?.[0];
      const signUrl = invitee?.signUrl || result?.data?.signUrl || result?.signUrl;
      // signRequestId is our BillSignRequest id (what /finalize looks up).
      const requestId = result?.signRequestId || result?.data?.requestId || result?.data?._id || result?.requestId || null;
      const documentId = result?.data?.documentId || result?.documentId || null;
      const startMessage = result?.messages?.[0]?.message || 'eSign request created successfully!';

      if (!signUrl) {
        throw new Error('Failed to get signing URL');
      }

      messageApi.destroy();
      messageApi.success(startMessage);

      setESignState({
        isLoading: false,
        requestId,
        documentId,
        signUrl,
        modalVisible: true,
        startMessage,
        error: null,
      });
      eSignWatcher.open(signUrl); // may be popup-blocked after the await; the modal has a button

    } catch (error) {
      messageApi.destroy();
      messageApi.error('Failed to start eSign: ' + (error?.message || error));
      setESignState({ ...eSignState, isLoading: false });
    }
  };

  const handleESignComplete = async () => {
    try {
      if (!eSignState.requestId) {
        messageApi.error('Signing request reference is missing.');
        return;
      }
      setESignState({ ...eSignState, isLoading: true });
      messageApi.loading('Finalizing eSign and creating bill...');

      // Finalize the eSign and create the bill
      const result = await finalizeESign(eSignState.requestId, eSignState.documentId);

      if (result.success) {
        messageApi.success('Bill created successfully with eSign!');

        // Reset form
        setForm({
          aadhaarNo: customer?.aadhaar || '',
          goldDetails: [
            {
              itemName: '',
              purity: '',
              netWeight: 0,
              grossWeight: 0,
              deduction: 0,
              ratePerGram: ratePerGram || 0,
              totalPrice: 0,
            },
          ],
          paymentMode: 'CASH',
          remarks: '',
          branchLocation: '',
          billId: generateBillId(),
          billDate: new Date(),
          dueDate: new Date(),
          salesRepName: 'Our company name',
          customerId: customer?._id || '',
          gstPercent: 3,
          processingFees: 0,
          images: [],
        });

        // Reset eSign state
        setESignState({
          isLoading: false,
          requestId: null,
          documentId: null,
          signUrl: null,
          modalVisible: false,
          startMessage: '',
        });

        // Navigate to view created bill
        navigate('/billing/viewCreatedBill', { state: { bill: result.data.bill } });
      } else {
        throw new Error(result.message || 'Failed to finalize eSign');
      }
    } catch (error) {
      messageApi.destroy();
      messageApi.error('Failed to finalize eSign: ' + (error?.message || error));
      setESignState({ ...eSignState, isLoading: false });
    }
  };

  // Leegality's webhook / the signer's return marks the sign request on the
  // server and pushes `esign.updated`. Completed → finalize (creates/returns the
  // bill) automatically; failed → say so. Hints re-read our own record only.
  const finalizingRef = useRef(false);
  const eSignWatcher = useESignWatcher({
    documentId: eSignState.documentId,
    active: eSignState.modalVisible,
    onSignal: async (payload) => {
      if (!eSignState.documentId || finalizingRef.current) return;
      let status = payload?.status;
      let error = payload?.error;
      if (!status) {
        try {
          const rec = await getBillSignRecord(eSignState.documentId);
          status = rec?.status;
          error = rec?.webhook?.error;
        } catch (e) { return; }
      }
      if (status === 'completed') {
        finalizingRef.current = true;
        eSignWatcher.closePopup();
        try { await handleESignComplete(); } finally { finalizingRef.current = false; }
      } else if (status === 'failed') {
        setESignState((prev) => ({ ...prev, error: error || 'The signer rejected the document or the invitation expired.' }));
      }
    },
  });

  const handleESignCancel = () => {
    eSignWatcher.closePopup();
    setESignState({
      isLoading: false,
      requestId: null,
      documentId: null,
      signUrl: null,
      modalVisible: false,
      startMessage: '',
    });
    messageApi.info('eSign cancelled');
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    try {
      const response = await createBill(form);
      setForm({
        aadhaarNo: customer?.aadhaar || '',
        goldDetails: [
          {
            itemName: '',
            purity: '',
            netWeight: 0,
            grossWeight: 0,
            deduction: 0,
            ratePerGram: ratePerGram || 0,
            totalPrice: 0,
          },
        ],
        paymentMode: 'CASH',
        remarks: '',
        branchLocation: '',
        billId: generateBillId(),
        billDate: new Date(),
        dueDate: new Date(),
        salesRepName: 'Our company name',
        customerId: customer?._id || '',
        gstPercent: 3,
        processingFees: 0,
        images: [],
      });
      navigate('/billing/viewCreatedBill', { state: { bill: response } });
    } catch (error) {
      messageApi.error('Failed to create bill: ' + (error?.message || error));
    }
  };

  return (
    <>
      {contextHolder}
      <div className="container-fluid px-2 px-md-4 py-3" style={{ maxWidth: 900, margin: 'auto' }}>
        <Card id="bill-to-print"
          style={{
            backgroundColor: '#fff',
            borderRadius: 6,
            boxShadow: 'none',
            padding: screens.xs ? 12 : 24,
            margin: 'auto',
          }}
        >
          <Title level={3} style={{ textAlign: 'center', marginBottom: 12 }}>
            Invoice
          </Title>

          {/* Company Info */}
          <div className="print-section company-info">
            <CompanyInfo logo={aurikshaLogo} companyInfo={companyInfo} billDate={form.billDate} billId={form.billId} />
          </div>
          {/* Customer Details (left) and GoldImageCapture (right) side-by-side */}
          <div className="print-section no-break" style={{ marginBottom: '8px', marginTop: 12 }}>
            {customer && (
              <CustomerDetails
                customer={customer}
                images={form.images}
                setImages={(imgs) => setForm({ ...form, images: imgs })}
                customerImage={form.customerImage}
                setCustomerImage={(img) => setForm({ ...form, customerImage: img })}
              />
            )}
          </div>

          {/* Gold Items Table */}
          <div className="print-section no-break">
            <GoldItemsTable
              showDeleteOption={true}
              ratePerGram={ratePerGram}
              goldDetails={form.goldDetails}
              onChange={handleGoldDetailsChange}
              processingFees={form.processingFees}
              onProcessingFeesChange={(fees) => setForm({ ...form, processingFees: fees })}
            />
          </div>
          

          <Divider style={{ margin: '16px 0' }} />

          {/* Payment and Remarks */}
          <div style={{ marginBottom: '8px' }}>
            <PaymentAndRemarks form={form} setForm={setForm} />
          </div>
          {/* Action buttons kept outside printable Card so they are not included in print */}
          <Row className="no-print" style={{ marginTop: 16, justifyContent: 'center', gap: 12 }}>
            <Col>
              <Button type="primary" size="large" onClick={handleSubmit} aria-label="Create Bill">
                Create Bill
              </Button>
            </Col>
            <Col>
              <Button
                type="default"
                size="large"
                onClick={handleESignSubmit}
                loading={eSignState.isLoading}
                disabled={!customer || !customer.aadhaar}
                aria-label="Create Bill with eSign"
              >
                Create with eSign
              </Button>
            </Col>
            <Col>
              <Button size="large" onClick={() => window.print()}>
                Print
              </Button>
            </Col>
          </Row>
          <div style={{ pageBreakAfter: 'always', breakAfter: 'page' }} />
          {/* Gold Purchase Declaration */}
          <div style={{ marginTop: '8px' }}>
            <GoldPurchaseDeclaration billData={form} customerData={customer} />
          </div>





        </Card>



        {/* eSign Completion Modal */}
        <Modal
          title="Aadhaar eSign"
          open={eSignState.modalVisible}
          onOk={handleESignComplete}
          onCancel={handleESignCancel}
          okText="Check status now"
          okButtonProps={{ type: 'default' }}
          cancelText="Close"
          confirmLoading={eSignState.isLoading}
          closable={!eSignState.isLoading}
          maskClosable={false}
          keyboard={false}
          width={720}
        >
          <div style={{ padding: '8px 0' }}>
            {eSignState.isLoading ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Spin tip="Processing..." />
              </div>
            ) : (
              <>
                {eSignState.startMessage && (
                  <Typography.Text type="success" style={{ display: 'block', marginBottom: 12 }}>
                    {eSignState.startMessage}
                  </Typography.Text>
                )}
                {eSignState.error ? (
                  <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Signing was not completed" description={eSignState.error} />
                ) : (
                  <Typography.Paragraph style={{ marginBottom: 12 }}>
                    <Spin size="small" style={{ marginRight: 8 }} />
                    Waiting for the customer to sign on Leegality. The bill opens automatically the moment signing completes.
                  </Typography.Paragraph>
                )}
                {eSignWatcher.popupBlocked && (
                  <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="The browser blocked the signing window — use “Open Signing Page”." />
                )}
                {eSignWatcher.popupClosed && !eSignState.error && (
                  <Alert type="info" showIcon style={{ marginBottom: 12 }} message="The signing window was closed. If the customer finished signing, this updates automatically; otherwise reopen the signing page." />
                )}
                <Row gutter={[12, 12]}>
                  <Col span={12}>
                    <Button block type="primary" onClick={() => eSignWatcher.open(eSignState.signUrl)}>
                      Open Signing Page
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      onClick={() => {
                        navigator.clipboard.writeText(eSignState.signUrl || '');
                        messageApi.success('Signing link copied!');
                      }}
                    >
                      Copy Signing Link
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </Modal>

        {/* Print-specific styling using @media print */}
        <style>
          {`
@media print {

  /* Remove browser default margins */
  @page {
    size: A4 portrait;
    margin: 0;
  }

  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
  }

  /* Remove app layout spacing */
  #root,
  .ant-layout,
  .ant-layout-content,
  .container-fluid {
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Hide sidebar / header / buttons */
  .ant-layout-sider,
  .ant-layout-header,
  .no-print,
  button {
    display: none !important;
  }

  /* Make invoice full printable width */
  #bill-to-print {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 5mm !important;  /* Proper print padding */
    box-shadow: none !important;
    border: none !important;
  }

  /* Prevent awkward breaks */
  .print-section,
  .no-break {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  /* Tables */
  table {
    width: 100% !important;
    border-collapse: collapse !important;
  }

  thead {
    display: table-header-group !important;
  }

  tr {
    page-break-inside: avoid !important;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

`}
        </style>
      </div>
    </>
  );
};

export default CreateBill;
